import {
	Button,
	ButtonGroup,
	Checkbox,
	Heading,
	IconButton,
	LuIcon,
	Menu,
	Separator,
	Text,
	toast,
} from "@overmap-ai/blocks"
import {
	type Asset,
	type AssetType,
	type AssetTypeFieldValues,
	type AssetTypeIdentifier,
	type AssetTypeIdentifierValue,
	type AssetTypeStatus,
	type Organization,
	type Procedure,
	type ProcedureFieldValues,
	type ProcedureInitiative,
	type ProcedureStep,
	type ProcedureStepAssignee,
	type ProcedureStepFieldValues,
	type ProcedureStepFieldValuesReview,
	type ProcedureStepReviewer,
	type ProcedureType,
	type Project,
	type Stored,
	submittedAtModelSortFn,
	toFileNameSafeString,
} from "@overmap-ai/common"
import {
	selectAssets,
	selectAssetTypeFields,
	selectAssetTypeFieldValues,
	selectAssetTypeIdentifiers,
	selectAssetTypeIdentifierValues,
	selectAssetTypesOfOrganization,
	selectAssetTypeStatuses,
	selectLatestFieldsByProcedureStep,
	selectProcedureFieldValues,
	selectProcedureInitiativesByIds,
	selectProcedureInitiativesOfProject,
	selectProceduresOfProject,
	selectProcedureStepAssignees,
	selectProcedureStepFieldValues,
	selectProcedureStepFieldValuesReviews,
	selectProcedureStepReviewers,
	selectProcedureSteps,
	selectProcedureTypeFields,
	selectProcedureTypesOfOrganization,
	selectUsersOfProject,
} from "@overmap-ai/core"
import { ConditionManager, FieldSchema } from "@overmap-ai/forms"
import { useSort } from "@overmap-ai/hooks"
import { getRouteApi, Link, useLocation, useNavigate } from "@tanstack/react-router"
import { cx } from "class-variance-authority"
import { saveAs } from "file-saver"
import JSZip from "jszip"
import Papa from "papaparse"
import { memo, useCallback, useMemo, useState } from "react"

import { ProcedureInitiativeCard } from "../../components/Cards/ProcedureInitiativeCard"
import { ViewContainer } from "../../components/Containers/ViewContainer"
import { ProcedureInitiativeFilterMenu } from "../../components/FilterMenus/ProcedureInitiativeFilterMenu"
import { computeProcedureInitiativeIndex } from "../../components/IndexLabels/utils"
import { getProcedureInitiativeDataCsvExporter } from "../../csv/CSVExporter/procedures"
import { cardLayoutCva, interactiveCardCva } from "../../cva"
import { FilterRenderer } from "../../filtering/components"
import type { AnyFilter } from "../../filtering/filter"
import { useFilterManager, useFilters } from "../../filtering/hooks"
import { getProcedureInitiativeFilterSchema } from "../../filtering/schemas"
import { useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import type { RootState } from "../../store"
import {
	appStore,
	setProcedureInitiativeFilters,
	useAppDispatch,
	useAppSelector,
	useShallowEqaulSelector,
} from "../../store"
import { selectProcedureInitiativeFilters } from "../../store/selectors"
import type { ProcedureData, ProcedureInitiativeData } from "../../typings"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/$projectId/_projectMap/initiatives/")

export const ProcedureInitiativesView = memo(() => {
	const { organizationId, projectId } = routeApi.useParams()

	const procedureInitiatives = useShallowEqaulSelector(selectProcedureInitiativesOfProject(projectId))
	const sortedProcedureInitiatives = useSort(procedureInitiatives, submittedAtModelSortFn)
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const location = useLocation()
	const openConfirmDeleteDialog = useConfirmDeleteDialog()
	const users = useShallowEqaulSelector(selectUsersOfProject(projectId))
	const assetTypes = useShallowEqaulSelector(selectAssetTypesOfOrganization(organizationId))
	const procedureTypes = useShallowEqaulSelector(selectProcedureTypesOfOrganization(organizationId))
	const serializedFilters = useAppSelector(selectProcedureInitiativeFilters)
	const [selectedProcedureInitiativeIds, setSelectedProcedureInitiativeIds] = useState<
		Set<ProcedureInitiative["uuid"]>
	>(new Set())
	const [isExporting, setIsExporting] = useState(false)

	const filterSchema = useMemo(
		() =>
			getProcedureInitiativeFilterSchema({
				assetTypes: assetTypes,
				procedureTypes,
				users,
			}),
		[procedureTypes, assetTypes, users],
	)

	const filters = useMemo(() => {
		return filterSchema.deserializeFilters(serializedFilters)
	}, [filterSchema, serializedFilters])

	const handleFiltersChange = useCallback(
		(filters: AnyFilter[]) => {
			dispatch(setProcedureInitiativeFilters(filterSchema.serializeFilters(filters)))
		},
		[dispatch, filterSchema],
	)

	const filterManager = useFilterManager({
		filterSchema,
		filters,
		onFilterChange: handleFiltersChange,
	})

	const filteredProcedureInitiatives = useFilters({
		filters,
		data: sortedProcedureInitiatives,
		filterManager,
	})

	const selectedProcedureInitiatives = useMemo(
		() =>
			sortedProcedureInitiatives.filter((procedureInitiative) =>
				selectedProcedureInitiativeIds.has(procedureInitiative.uuid),
			),
		[sortedProcedureInitiatives, selectedProcedureInitiativeIds],
	)
	const areAllFilteredProcedureInitiativesSelected =
		filteredProcedureInitiatives.length > 0 &&
		filteredProcedureInitiatives.every(({ uuid }) => selectedProcedureInitiativeIds.has(uuid))
	const areSomeFilteredProcedureInitiativesSelected = filteredProcedureInitiatives.some(({ uuid }) =>
		selectedProcedureInitiativeIds.has(uuid),
	)

	const handleCardClick = useCallback(
		(procedureInitiative: Stored<ProcedureInitiative>) => {
			void navigate({
				from: "/$organizationId/$projectId/initiatives/",
				to: "/$organizationId/$projectId/initiatives/$procedureInitiativeId",
				params: (prev) => ({ ...prev, procedureInitiativeId: procedureInitiative.uuid }),
			})
		},
		[navigate],
	)

	const handleViewProcedureInitiative = useCallback(
		(procedureInitiative: Stored<ProcedureInitiative>) => {
			void navigate({
				from: "/$organizationId/$projectId/initiatives/",
				to: "/$organizationId/$projectId/initiatives/$procedureInitiativeId",
				params: (prev) => ({ ...prev, procedureInitiativeId: procedureInitiative.uuid }),
			})
		},
		[navigate],
	)

	const handleDeleteProcedureInitiative = useCallback(
		(procedureInitiative: Stored<ProcedureInitiative>) => {
			openConfirmDeleteDialog({
				title: "Delete initiave",
				description: "Are you sure you want to delete this initiave? This action cannot be undone.",
				onAction: () => {
					void sdk.procedureInitiatives.delete(procedureInitiative.uuid)
				},
			})
		},
		[openConfirmDeleteDialog],
	)

	const handleProcedureInitiativeSelectedChange = useCallback(
		(procedureInitiative: Stored<ProcedureInitiative>, selected: boolean) => {
			setSelectedProcedureInitiativeIds((current) => {
				const next = new Set(current)

				if (selected) next.add(procedureInitiative.uuid)
				else next.delete(procedureInitiative.uuid)

				return next
			})
		},
		[],
	)

	const handleToggleAllFilteredProcedureInitiatives = useCallback(
		(selected: boolean) => {
			setSelectedProcedureInitiativeIds((current) => {
				const next = new Set(current)

				for (const { uuid } of filteredProcedureInitiatives) {
					if (selected) next.add(uuid)
					else next.delete(uuid)
				}

				return next
			})
		},
		[filteredProcedureInitiatives],
	)

	const handleExportProcedureInitiatives = useCallback(async () => {
		if (isExporting || selectedProcedureInitiatives.length === 0) return

		setIsExporting(true)

		try {
			const exported = await exportProcedureInitiatives(
				organizationId,
				projectId,
				selectedProcedureInitiatives.map(({ uuid }) => uuid),
			)

			if (!exported) {
				toast.error({
					title: "Nothing to export",
					description: "The selected procedure initiatives do not contain any procedures.",
				})
			}
		} catch {
			toast.error({
				title: "Export failed",
				description: "The procedure initiative CSV export could not be created.",
			})
		} finally {
			setIsExporting(false)
		}
	}, [isExporting, organizationId, projectId, selectedProcedureInitiatives])

	return (
		<ViewContainer maxWidth="6xl">
			<div className="flex size-full flex-col gap-4">
				<div className="flex flex-col gap-1">
					<div className="flex justify-between gap-2">
						<Heading size="xl">Initiatives</Heading>

						<Link
							from="/$organizationId/$projectId/initiatives/"
							to="/$organizationId/$projectId/initiatives/create"
							search={{ redirect: location.href }}
						>
							<Button variant="surface" size="sm">
								<LuIcon icon={modelIconMapping.add} />
								Create
							</Button>
						</Link>
					</div>

					<Text size="sm" accentColor="base">
						Initiatives help you track concurrent procedures across assets.
					</Text>
				</div>

				<Separator size="full" />

				<div className="flex flex-col gap-2">
					<ButtonGroup className="flex justify-between gap-2" size="sm" variant="soft" accentColor="base">
						<div className="flex items-center gap-2">
							<div className="flex items-center gap-2">
								<Checkbox.Root
									checked={
										areAllFilteredProcedureInitiativesSelected
											? true
											: areSomeFilteredProcedureInitiativesSelected
												? "indeterminate"
												: false
									}
									onCheckedChange={handleToggleAllFilteredProcedureInitiatives}
									disabled={filteredProcedureInitiatives.length === 0}
									size="sm"
								>
									<Checkbox.Indicator>
										<LuIcon icon={modelIconMapping.check} />
									</Checkbox.Indicator>
								</Checkbox.Root>
								<Text size="sm" accentColor="base">
									Select all
								</Text>
							</div>
							<ProcedureInitiativeFilterMenu
								filterManager={filterManager}
								procedureTypes={procedureTypes}
								assetTypes={assetTypes}
								users={users}
								size="sm"
								align="start"
							>
								<Button>
									<LuIcon icon={modelIconMapping.filter} />
									Filter
								</Button>
							</ProcedureInitiativeFilterMenu>
							{filterManager.getFilters().length > 0 && (
								<IconButton onClick={filterManager.removeAll}>
									<LuIcon icon={modelIconMapping.close} />
								</IconButton>
							)}
						</div>

						<div className="flex items-center justify-end gap-2">
							{selectedProcedureInitiatives.length > 0 && (
								<Menu.Root>
									<Menu.ClickTrigger>
										<Button>
											<LuIcon icon={modelIconMapping.command} />
											Actions
										</Button>
									</Menu.ClickTrigger>
									<Menu.Content size="sm">
										<Menu.Item
											onSelect={() => void handleExportProcedureInitiatives()}
											disabled={isExporting}
										>
											<LuIcon icon={modelIconMapping.download} />
											{isExporting ? "Exporting…" : "Export CSV"}
										</Menu.Item>
									</Menu.Content>
								</Menu.Root>
							)}
						</div>
					</ButtonGroup>

					<FilterRenderer filterManager={filterManager} size="sm" />
				</div>

				<div className="oveflow-y-auto size-full gap-4 overflow-x-hidden">
					<div className={cardLayoutCva()}>
						{filteredProcedureInitiatives.length > 0 ? (
							filteredProcedureInitiatives.map((procedureInitiative) => (
								<ProcedureInitiativeCard
									key={procedureInitiative.uuid}
									variant="surface"
									onClick={handleCardClick}
									onView={handleViewProcedureInitiative}
									onDelete={handleDeleteProcedureInitiative}
									selectable
									selected={selectedProcedureInitiativeIds.has(procedureInitiative.uuid)}
									onSelectedChange={handleProcedureInitiativeSelectedChange}
									procedureInitiative={procedureInitiative}
									className={cx(interactiveCardCva(), "h-64 grow cursor-pointer")}
									size="sm"
								/>
							))
						) : (
							<Text accentColor="base" weight="regular" size="sm">
								No initiatives.
							</Text>
						)}
					</div>
				</div>
			</div>
		</ViewContainer>
	)
})
ProcedureInitiativesView.displayName = "ProcedureInitiativesView"

async function exportProcedureInitiatives(
	organizationId: Organization["uuid"],
	projectId: Project["uuid"],
	procedureInitiativeIds: ProcedureInitiative["uuid"][],
) {
	const state = appStore.getState()
	const procedureInitiatives = selectProcedureInitiativesByIds(procedureInitiativeIds)(state).filter(
		({ project }) => project === projectId,
	)
	const procedureInitiativeDataByInitiative = getProcedureInitiativesData(state, projectId, procedureInitiativeIds)
	const selectedProcedureTypeIds = new Set(procedureInitiatives.map(({ procedure_type }) => procedure_type))
	const procedureSteps = selectProcedureSteps(state).filter(({ procedure_type }) =>
		selectedProcedureTypeIds.has(procedure_type),
	)
	const procedureTypesById = Object.fromEntries(
		selectProcedureTypesOfOrganization(organizationId)(state).map((procedureType) => [
			procedureType.uuid,
			procedureType,
		]),
	)
	const procedureStepsByProcedureType: Record<ProcedureType["uuid"], Stored<ProcedureStep>[]> = {}
	const procedureStepFieldSchemas: Record<ProcedureStep["uuid"], [FieldSchema, ConditionManager]> = {}
	const latestFieldsByProcedureStep = selectLatestFieldsByProcedureStep(state)

	for (const procedureStep of procedureSteps) {
		if (!procedureStepsByProcedureType[procedureStep.procedure_type]) {
			procedureStepsByProcedureType[procedureStep.procedure_type] = []
		}
		procedureStepsByProcedureType[procedureStep.procedure_type]!.push(procedureStep)

		const latestFields = latestFieldsByProcedureStep[procedureStep.uuid]
		const fieldSchema = FieldSchema.deserialize(latestFields?.fields ?? [])
		const conditions = fieldSchema.deserializeConditions(latestFields?.field_conditions ?? [])
		procedureStepFieldSchemas[procedureStep.uuid] = [fieldSchema, new ConditionManager(conditions)]
	}

	const latestAssetTypeFieldsByAssetType: Record<
		AssetType["uuid"],
		ReturnType<typeof selectAssetTypeFields>[number] | undefined
	> = {}
	for (const fields of selectAssetTypeFields(state)) {
		const current = latestAssetTypeFieldsByAssetType[fields.asset_type]
		if (!current || current.submitted_at < fields.submitted_at) {
			latestAssetTypeFieldsByAssetType[fields.asset_type] = fields
		}
	}

	const latestProcedureTypeFieldsByProcedureType: Record<
		ProcedureType["uuid"],
		ReturnType<typeof selectProcedureTypeFields>[number] | undefined
	> = {}
	for (const fields of selectProcedureTypeFields(state)) {
		const current = latestProcedureTypeFieldsByProcedureType[fields.procedure_type]
		if (!current || current.submitted_at < fields.submitted_at) {
			latestProcedureTypeFieldsByProcedureType[fields.procedure_type] = fields
		}
	}

	const assetTypeIdentifiersByAssetType: Record<AssetType["uuid"], Stored<AssetTypeIdentifier>[]> = {}
	for (const identifier of selectAssetTypeIdentifiers(state)) {
		if (!assetTypeIdentifiersByAssetType[identifier.asset_type]) {
			assetTypeIdentifiersByAssetType[identifier.asset_type] = []
		}
		assetTypeIdentifiersByAssetType[identifier.asset_type]!.push(identifier)
	}

	const assetTypeStatusesByAssetType: Record<AssetType["uuid"], Stored<AssetTypeStatus>[]> = {}
	for (const status of selectAssetTypeStatuses(state)) {
		if (!assetTypeStatusesByAssetType[status.asset_type]) assetTypeStatusesByAssetType[status.asset_type] = []
		assetTypeStatusesByAssetType[status.asset_type]!.push(status)
	}

	const zip = new JSZip()
	const fileNameCounts: Record<string, number> = {}
	let exportedFileCount = 0

	for (const procedureInitiative of procedureInitiatives) {
		const data = procedureInitiativeDataByInitiative[procedureInitiative.uuid] ?? []
		if (data.length === 0) continue

		const procedureType = procedureTypesById[procedureInitiative.procedure_type]
		if (!procedureType) {
			throw new Error(`Procedure type ${procedureInitiative.procedure_type} not found in store.`)
		}

		const procedureTypeFieldSchema = FieldSchema.deserialize(
			latestProcedureTypeFieldsByProcedureType[procedureInitiative.procedure_type]?.fields ?? [],
		)
		const assetTypeFieldSchema = FieldSchema.deserialize(
			latestAssetTypeFieldsByAssetType[procedureInitiative.asset_type]?.fields ?? [],
		)
		const exporter = getProcedureInitiativeDataCsvExporter(data, {
			procedureSteps: procedureStepsByProcedureType[procedureInitiative.procedure_type] ?? [],
			procedureTypeFieldSchema,
			procedureStepFieldSchemas,
			assetTypeIdentifiers: assetTypeIdentifiersByAssetType[procedureInitiative.asset_type] ?? [],
			assetTypeFieldSchema,
			assetTypeStatuses: assetTypeStatusesByAssetType[procedureInitiative.asset_type] ?? [],
		})
		const csvData = Papa.unparse(exporter.toPapaUnparseFormat(), { quotes: true })
		const fileName = computeProcedureInitiativeIndex(procedureType, procedureInitiative)
		const count = (fileNameCounts[fileName] ?? 0) + 1
		fileNameCounts[fileName] = count
		const uniqueFileName = count > 1 ? `${fileName} (${count})` : fileName

		zip.file(toFileNameSafeString(uniqueFileName, ".csv"), csvData)
		exportedFileCount += 1
	}

	if (exportedFileCount === 0) return false

	const blob = await zip.generateAsync({ type: "blob" })
	saveAs(blob, toFileNameSafeString("Procedure initiatives", ".zip"))
	return true
}

function getProcedureInitiativesData(
	state: RootState,
	projectId: Project["uuid"],
	procedureInitiativeIds: ProcedureInitiative["uuid"][],
) {
	const procedures = selectProceduresOfProject(projectId)(state)
	const assets = selectAssets(state)
	const assetTypeFieldValues = selectAssetTypeFieldValues(state)
	const assetTypeIdentifierValues = selectAssetTypeIdentifierValues(state)
	const procedureFieldValues = selectProcedureFieldValues(state)
	const procedureStepAssignees = selectProcedureStepAssignees(state)
	const procedureStepReviewers = selectProcedureStepReviewers(state)
	const procedureStepFieldValues = selectProcedureStepFieldValues(state)
	const procedureStepFieldValuesReviews = selectProcedureStepFieldValuesReviews(state)
	const procedureInitiativeIdsSet = new Set(procedureInitiativeIds)
	const selectedProcedures = procedures.filter(
		(procedure) =>
			!!procedure.procedure_initiative && procedureInitiativeIdsSet.has(procedure.procedure_initiative),
	)
	const procedureIds = new Set(selectedProcedures.map(({ uuid }) => uuid))
	const assetIds = new Set(selectedProcedures.map(({ asset }) => asset))
	const assetsById: Record<Asset["uuid"], Stored<Asset>> = {}
	const latestAssetTypeFieldValuesByAsset: Record<Asset["uuid"], Stored<AssetTypeFieldValues> | undefined> = {}
	const latestAssetTypeIdentifierValuesByAsset: Record<
		Asset["uuid"],
		Record<AssetTypeIdentifier["uuid"], Stored<AssetTypeIdentifierValue> | undefined> | undefined
	> = {}
	const latestProcedureFieldValuesByProcedure: Record<Procedure["uuid"], Stored<ProcedureFieldValues> | undefined> =
		{}
	const latestProcedureStepFieldValuesByProcedure: Record<
		Procedure["uuid"],
		Record<ProcedureStep["uuid"], Stored<ProcedureStepFieldValues> | undefined> | undefined
	> = {}
	const latestProcedureStepFieldValuesReviewByFieldValues: Record<
		ProcedureStepFieldValues["uuid"],
		Stored<ProcedureStepFieldValuesReview> | undefined
	> = {}
	const procedureStepAssigneesByProcedure: Record<
		Procedure["uuid"],
		Record<ProcedureStep["uuid"], Stored<ProcedureStepAssignee>[] | undefined> | undefined
	> = {}
	const procedureStepReviewersByProcedure: Record<
		Procedure["uuid"],
		Record<ProcedureStep["uuid"], Stored<ProcedureStepReviewer>[] | undefined> | undefined
	> = {}

	for (const asset of assets) {
		if (assetIds.has(asset.uuid)) assetsById[asset.uuid] = asset
	}

	for (const fieldValues of assetTypeFieldValues) {
		if (!assetIds.has(fieldValues.asset)) continue
		const current = latestAssetTypeFieldValuesByAsset[fieldValues.asset]
		if (!current || current.submitted_at < fieldValues.submitted_at) {
			latestAssetTypeFieldValuesByAsset[fieldValues.asset] = fieldValues
		}
	}

	for (const identifierValue of assetTypeIdentifierValues) {
		const { asset, asset_type_identifier } = identifierValue
		if (!assetIds.has(asset)) continue
		if (!(asset in latestAssetTypeIdentifierValuesByAsset)) latestAssetTypeIdentifierValuesByAsset[asset] = {}

		const current = latestAssetTypeIdentifierValuesByAsset[asset]![asset_type_identifier]
		if (!current || current.submitted_at <= identifierValue.submitted_at) {
			latestAssetTypeIdentifierValuesByAsset[asset]![asset_type_identifier] = identifierValue
		}
	}

	for (const fieldValues of procedureFieldValues) {
		if (!procedureIds.has(fieldValues.procedure)) continue
		const current = latestProcedureFieldValuesByProcedure[fieldValues.procedure]
		if (!current || current.submitted_at < fieldValues.submitted_at) {
			latestProcedureFieldValuesByProcedure[fieldValues.procedure] = fieldValues
		}
	}

	for (const fieldValues of procedureStepFieldValues) {
		const { procedure, procedure_step } = fieldValues
		if (!procedureIds.has(procedure)) continue
		if (!(procedure in latestProcedureStepFieldValuesByProcedure)) {
			latestProcedureStepFieldValuesByProcedure[procedure] = {}
		}

		const current = latestProcedureStepFieldValuesByProcedure[procedure]![procedure_step]
		if (!current || current.submitted_at < fieldValues.submitted_at) {
			latestProcedureStepFieldValuesByProcedure[procedure]![procedure_step] = fieldValues
		}
	}

	for (const review of procedureStepFieldValuesReviews) {
		const current = latestProcedureStepFieldValuesReviewByFieldValues[review.field_values]
		if (!current || current.submitted_at < review.submitted_at) {
			latestProcedureStepFieldValuesReviewByFieldValues[review.field_values] = review
		}
	}

	for (const assignee of procedureStepAssignees) {
		const { procedure, procedure_step } = assignee
		if (!procedureIds.has(procedure)) continue
		if (!(procedure in procedureStepAssigneesByProcedure)) procedureStepAssigneesByProcedure[procedure] = {}
		if (!(procedure_step in procedureStepAssigneesByProcedure[procedure]!)) {
			procedureStepAssigneesByProcedure[procedure]![procedure_step] = []
		}
		procedureStepAssigneesByProcedure[procedure]![procedure_step]!.push(assignee)
	}

	for (const reviewer of procedureStepReviewers) {
		const { procedure, procedure_step } = reviewer
		if (!procedureIds.has(procedure)) continue
		if (!(procedure in procedureStepReviewersByProcedure)) procedureStepReviewersByProcedure[procedure] = {}
		if (!(procedure_step in procedureStepReviewersByProcedure[procedure]!)) {
			procedureStepReviewersByProcedure[procedure]![procedure_step] = []
		}
		procedureStepReviewersByProcedure[procedure]![procedure_step]!.push(reviewer)
	}

	const ret: Record<ProcedureInitiative["uuid"], ProcedureInitiativeData[]> = Object.fromEntries(
		procedureInitiativeIds.map((procedureInitiativeId) => [procedureInitiativeId, []]),
	)

	for (const procedure of selectedProcedures) {
		const procedureInitiativeId = procedure.procedure_initiative!
		const asset = assetsById[procedure.asset]
		if (!asset) throw new Error(`Asset ${procedure.asset} of procedure ${procedure.uuid} not found in store.`)

		const latestProcedureStepFieldValues = latestProcedureStepFieldValuesByProcedure[procedure.uuid] ?? {}
		const latestProcedureStepFieldValuesReviews: ProcedureData["procedureStepFieldValuesReviews"] = {}
		for (const [procedureStepId, fieldValues] of Object.entries(latestProcedureStepFieldValues)) {
			if (!fieldValues) continue
			latestProcedureStepFieldValuesReviews[procedureStepId] =
				latestProcedureStepFieldValuesReviewByFieldValues[fieldValues.uuid]
		}

		ret[procedureInitiativeId].push({
			procedure,
			asset,
			assetTypeFieldValues: latestAssetTypeFieldValuesByAsset[asset.uuid],
			assetTypeFieldValuesAttachments: [],
			assetTypeIdentifierValues: latestAssetTypeIdentifierValuesByAsset[asset.uuid] ?? {},
			procedureFieldValues: latestProcedureFieldValuesByProcedure[procedure.uuid],
			procedureStepFieldValues: latestProcedureStepFieldValues,
			procedureStepFieldValuesReviews: latestProcedureStepFieldValuesReviews,
			procedureStepAssignees: procedureStepAssigneesByProcedure[procedure.uuid] ?? {},
			procedureStepReviewers: procedureStepReviewersByProcedure[procedure.uuid] ?? {},
			procedureStepFieldValuesAttachments: {},
			procedureFieldValuesAttachments: [],
		})
	}

	return ret
}
